import api from './api';

export interface DashboardActivity {
  id: number;
  actor: string | null;
  event: string;
  auditableType: string;
  /** Join key. Kept alongside the label - other callers may key off it. */
  auditableId: number;
  /**
   * Ready-to-print identity for the row, composed server-side: "Order 9BWVKWCDXH"
   * for a Quote, "Product #12" for every other audited type. The feed is generic
   * over auditableType, so it must not carry per-type naming rules of its own.
   */
  auditableLabel: string;
  at: string | null;
}

export interface DashboardAtRisk {
  jobId: number;
  quoteId: number;
  /**
   * Display identity. camelCase here (not the Resources' snake_case
   * quote_reference) because this feed is a hand-built projection in
   * DashboardMetrics::atRisk, keyed like its jobId/quoteId neighbours.
   * quoteId remains the join key.
   */
  quoteReference?: string | null;
  track: string;
  state: string;
  readyAt: string | null;
}

export interface DashboardPayload {
  pipeline: Record<string, number>;
  production: { byState: Record<string, number>; wip: number; overdue: number; boardActions: number };
  atRisk: DashboardAtRisk[];
  queues: {
    proofsPending: number;
    /** Proofs a buyer sent back for changes - staff must issue a revision. */
    changesRequested: number;
    procurementToReconfirm: number;
    /** Open buy-list lines still to be bought - the "Buy list" sidebar badge. */
    buyListOpen: number;
    cataloguePending: number;
    reordersOpen: number;
    /** LT14: orders delivered (CLOSED) but still UNPAID/PARTIAL - money to chase. */
    unpaidDelivered: number;
  };
  activity: DashboardActivity[];
  valueBooked: { currency: string; amount: number } | null;
  kpis: {
    ordersThisWeek: number;
    bookedThisMonth: { currency: string; amount: number };
    outstanding: { currency: string; amount: number };
  } | null;
  trends: { weekStart: string; orders: number; bookedValue: number }[] | null;
}

export async function fetchDashboard(): Promise<DashboardPayload> {
  const { data } = await api.get<DashboardPayload>('/admin/dashboard');
  return data;
}
